import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import translation files directly for better performance
import enCommon from './locales/en/common.json';
import enNavigation from './locales/en/navigation.json';
import enForms from './locales/en/forms.json';
import enButtons from './locales/en/buttons.json';
import enMessages from './locales/en/messages.json';
import enPages from './locales/en/pages.json';
import enWorkflows from './locales/en/workflows.json';

import nlCommon from './locales/nl/common.json';
import nlNavigation from './locales/nl/navigation.json';
import nlForms from './locales/nl/forms.json';
import nlButtons from './locales/nl/buttons.json';
import nlMessages from './locales/nl/messages.json';
import nlPages from './locales/nl/pages.json';
import nlWorkflows from './locales/nl/workflows.json';

// Define resources
const resources = {
  en: {
    common: enCommon,
    navigation: enNavigation,
    forms: enForms,
    buttons: enButtons,
    messages: enMessages,
    pages: enPages,
    workflows: enWorkflows,
  },
  nl: {
    common: nlCommon,
    navigation: nlNavigation,
    forms: nlForms,
    buttons: nlButtons,
    messages: nlMessages,
    pages: nlPages,
    workflows: nlWorkflows,
  },
};

// Language detection options
const detectionOptions = {
  // Order of language detection methods
  order: ['localStorage', 'navigator', 'htmlTag'],
  
  // Cache user language in localStorage
  caches: ['localStorage'],
  
  // Key to store language in localStorage
  lookupLocalStorage: 'i18nextLng',
  
  // Don't lookup from subdomain
  lookupFromSubdomainIndex: 0,
  
  // Don't lookup from path
  lookupFromPathIndex: 0,
  
  // Exclude certain languages from detection
  excludeCacheFor: ['cimode'],
  
  // Check for language in HTML tag
  htmlTag: document.documentElement,
  
  // Convert language codes (e.g., en-US -> en)
  convertDetectedLanguage: (lng) => lng.split('-')[0],
};

// Initialize i18next
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Resources
    resources,
    
    // Fallback language
    fallbackLng: 'en',
    
    // Default namespace
    defaultNS: 'common',
    
    // Available namespaces
    ns: ['common', 'navigation', 'forms', 'buttons', 'messages', 'pages', 'workflows'],
    
    // Language detection
    detection: detectionOptions,
    
    // Debug mode (disable in production)
    debug: process.env.NODE_ENV === 'development',
    
    // Interpolation options
    interpolation: {
      // React already escapes values
      escapeValue: false,
      
      // Format function for custom formatting
      format: (value, format, lng) => {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        if (format === 'capitalize') return value.charAt(0).toUpperCase() + value.slice(1);
        return value;
      },
    },
    
    // React options
    react: {
      // Use Suspense for async loading
      useSuspense: false,

      // Bind i18n instance to component
      bindI18n: 'languageChanged loaded',

      // Bind store to component
      bindI18nStore: 'added removed',
      
      // How to handle trans component defaultValue
      transEmptyNodeValue: '',
      
      // Transform the node before passing it to React
      transSupportBasicHtmlNodes: true,
      
      // Which nodes to keep when trans component children are empty
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
    
    // Backend options (for HTTP loading if needed)
    backend: {
      // Path to load resources from
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      
      // Allow cross domain requests
      crossDomain: false,
      
      // Request timeout
      requestOptions: {
        cache: 'default',
      },
    },
    
    // Save missing keys
    saveMissing: process.env.NODE_ENV === 'development',
    
    // Missing key handler
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation key: ${ns}:${key} for language: ${lng}`);
      }
    },
    
    // Key separator
    keySeparator: '.',
    
    // Namespace separator
    nsSeparator: ':',
    
    // Plural separator
    pluralSeparator: '_',
    
    // Context separator
    contextSeparator: '_',
    
    // Return objects for keys
    returnObjects: false,
    
    // Return null for missing keys
    returnNull: false,
    
    // Return empty string for missing keys
    returnEmptyString: false,
    
    // Post process
    postProcess: false,
    
    // Clean code
    cleanCode: true,
    
    // Load all languages
    preload: ['en', 'nl'],
    
    // Whitelist languages
    supportedLngs: ['en', 'nl'],
    
    // Non explicit whitelist
    nonExplicitSupportedLngs: false,
    
    // Load language
    load: 'languageOnly',
    
    // Simple check for valid language
    simplifyPluralSuffix: true,
  });

// Export language change function
export const changeLanguage = (lng) => {
  return i18n.changeLanguage(lng);
};

// Export current language getter
export const getCurrentLanguage = () => {
  return i18n.language;
};

// Export supported languages
export const getSupportedLanguages = () => {
  return ['en', 'nl'];
};

// Export language labels
export const getLanguageLabels = () => {
  return {
    en: 'English',
    nl: 'Nederlands',
  };
};

// Export i18n instance
export default i18n;
